/**
 * SVG export for pixel-stretch collages via affine-clipped image triangles.
 */
import {
    appendAffineClippedImageUse,
    escapeSvgAttr,
    fmtSvgNum,
    type AffineImageTriangleSlice,
} from "./affineImageTriangle";
import {
    cropEdgeSourceQuad,
    frameEdgeDestQuad,
    pickConnectorEdges,
    type PixelStretchArtboard,
    type PixelStretchNode,
    edgeSegment,
    sampleCountForEdge,
} from "./pixelStretch";
import type { PixelRect, Point2, StretchEdge } from "./pixelStretch";

export interface PixelStretchRasterSource {
    dataUrl: string;
    w: number;
    h: number;
}

export interface PixelStretchSvgOptions {
    connectorMaxSamples?: number;
}

const IMAGE_ID = "psRasterImg";

function splitQuadToTriangles(
    srcQuad: [Point2, Point2, Point2, Point2],
    dstQuad: [Point2, Point2, Point2, Point2],
): AffineImageTriangleSlice[] {
    const [s0, s1, s2, s3] = srcQuad;
    const [d0, d1, d2, d3] = dstQuad;
    return [
        {
            src: [s0.x, s0.y, s1.x, s1.y, s2.x, s2.y],
            dst: [d0.x, d0.y, d1.x, d1.y, d2.x, d2.y],
        },
        {
            src: [s0.x, s0.y, s2.x, s2.y, s3.x, s3.y],
            dst: [d0.x, d0.y, d2.x, d2.y, d3.x, d3.y],
        },
    ];
}

/** Build axis-aligned clip rect for a node frame with correct image transform. */
function appendNodeClip(
    defs: string[],
    body: string[],
    raster: PixelStretchRasterSource,
    node: PixelStretchNode,
    clipIdxRef: { value: number },
): void {
    const { crop, frame } = node;
    if (crop.w <= 0 || crop.h <= 0 || frame.w <= 0 || frame.h <= 0) return;

    const clipId = `psNodeClip${clipIdxRef.value++}`;
    const sx = raster.w / crop.w;
    const sy = raster.h / crop.h;
    const tx = frame.x - crop.x * sx;
    const ty = frame.y - crop.y * sy;

    defs.push(
        `<clipPath id="${clipId}" clipPathUnits="userSpaceOnUse">` +
            `<rect x="${fmtSvgNum(frame.x)}" y="${fmtSvgNum(frame.y)}" ` +
            `width="${fmtSvgNum(frame.w)}" height="${fmtSvgNum(frame.h)}" />` +
            `</clipPath>`,
    );
    body.push(
        `<g clip-path="url(#${clipId})">` +
            `<use href="#${IMAGE_ID}" xlink:href="#${IMAGE_ID}" ` +
            `transform="matrix(${fmtSvgNum(sx)} 0 0 ${fmtSvgNum(sy)} ${fmtSvgNum(tx)} ${fmtSvgNum(ty)})" />` +
            `</g>`,
    );
}

function appendConnectorSlices(
    defs: string[],
    body: string[],
    raster: PixelStretchRasterSource,
    nodeA: PixelStretchNode,
    nodeB: PixelStretchNode,
    maxSamples: number,
    clipIdxRef: { value: number },
): void {
    const { out, in: inEdge } = pickConnectorEdges(nodeA.frame, nodeB.frame);
    const aSeg = edgeSegment(nodeA.frame, out);
    const n = sampleCountForEdge(nodeA.crop, out, aSeg, maxSamples);

    for (let i = 0; i < n; i++) {
        const t0 = n === 1 ? 0 : i / (n - 1);
        const t1 = n === 1 ? 1 : (i + 1) / (n - 1);
        const srcQuad = connectorSliceSourceQuad(
            nodeA.crop,
            out,
            raster.w,
            raster.h,
            t0,
            t1,
        );
        const dstQuad = connectorSliceDestQuad(
            nodeA.frame,
            out,
            nodeB.frame,
            inEdge,
            t0,
            t1,
        );
        for (const slice of splitQuadToTriangles(srcQuad, dstQuad)) {
            appendAffineClippedImageUse(
                defs,
                body,
                IMAGE_ID,
                slice,
                "psConnClip",
                clipIdxRef,
            );
        }
    }
}

function connectorSliceSourceQuad(
    crop: PixelRect,
    edge: StretchEdge,
    imgW: number,
    imgH: number,
    t0: number,
    t1: number,
): [Point2, Point2, Point2, Point2] {
    const full = cropEdgeSourceQuad(crop, edge, imgW, imgH);
    const lerp = (a: Point2, b: Point2, t: number) => ({
        x: a.x + (b.x - a.x) * t,
        y: a.y + (b.y - a.y) * t,
    });
    const p0 = lerp(full[0], full[1], t0);
    const p1 = lerp(full[0], full[1], t1);
    const p2 = lerp(full[3], full[2], t1);
    const p3 = lerp(full[3], full[2], t0);
    return [p0, p1, p2, p3];
}

function connectorSliceDestQuad(
    frameA: PixelRect,
    outEdge: StretchEdge,
    frameB: PixelRect,
    inEdge: StretchEdge,
    t0: number,
    t1: number,
): [Point2, Point2, Point2, Point2] {
    const full = frameEdgeDestQuad(frameA, outEdge, frameB, inEdge);
    const lerp = (a: Point2, b: Point2, t: number) => ({
        x: a.x + (b.x - a.x) * t,
        y: a.y + (b.y - a.y) * t,
    });
    const p0 = lerp(full[0], full[1], t0);
    const p1 = lerp(full[0], full[1], t1);
    const p2 = lerp(full[3], full[2], t1);
    const p3 = lerp(full[3], full[2], t0);
    return [p0, p1, p2, p3];
}

export function buildPixelStretchSvg(
    raster: PixelStretchRasterSource,
    nodes: PixelStretchNode[],
    artboard: PixelStretchArtboard,
    options: PixelStretchSvgOptions = {},
): string {
    const maxSamples = options.connectorMaxSamples ?? 256;
    const escDataUrl = escapeSvgAttr(raster.dataUrl);
    const defs: string[] = [
        `<image id="${IMAGE_ID}" href="${escDataUrl}" xlink:href="${escDataUrl}" preserveAspectRatio="none" width="${fmtSvgNum(raster.w)}" height="${fmtSvgNum(raster.h)}" />`,
    ];
    const body: string[] = [];
    const clipIdxRef = { value: 0 };

    const bg = artboard.backgroundColor.replace(/^#/, "");
    if (bg.length >= 6) {
        body.push(
            `<rect x="0" y="0" width="${fmtSvgNum(artboard.width)}" height="${fmtSvgNum(artboard.height)}" fill="#${bg}" />`,
        );
    }

    for (let i = 0; i < nodes.length; i++) {
        if (i > 0) {
            appendConnectorSlices(
                defs,
                body,
                raster,
                nodes[i - 1],
                nodes[i],
                maxSamples,
                clipIdxRef,
            );
        }
    }

    for (const node of nodes) {
        appendNodeClip(defs, body, raster, node, clipIdxRef);
    }

    return (
        `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" ` +
        `viewBox="0 0 ${fmtSvgNum(artboard.width)} ${fmtSvgNum(artboard.height)}">` +
        `<defs>${defs.join("")}</defs>` +
        body.join("") +
        `</svg>`
    );
}
